import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

// If REACT_APP_BACKEND_URL is set (dev/preview), call that. Otherwise fall back
// to a same-origin relative path so the production build on Hostinger posts to
// /api/contact.php served right next to the static site.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    year: '',
    make: '',
    model: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // In dev/preview the FastAPI route is /api/contact. On the production
      // Hostinger build there's a PHP handler at /api/contact.php. We try the
      // PHP endpoint first when running same-origin (no BACKEND_URL set) and
      // fall back to /api/contact otherwise.
      const endpoint = BACKEND_URL ? `${API}/contact` : `${API}/contact.php`;
      const response = await axios.post(endpoint, formData);
      
      if (response.data.email_sent) {
        toast.success("Message Sent Successfully!", {
          description: "Thank you for contacting Tint Garage. We'll get back to you within 24 hours.",
          duration: 5000,
        });
      } else {
        toast.success("Message Received!", {
          description: "Your submission was saved. We'll contact you soon.",
          duration: 5000,
        });
      }
      
      setFormData({
        name: '',
        phone: '',
        email: '',
        year: '',
        make: '',
        model: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error("Submission Failed", {
        description: "Please try again or call us directly at (773) 690-2541.",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactCards = [
    {
      icon: MapPin,
      title: 'Location',
      content: <><p className="text-gray-400">9824 Industrial Dr<br />Bridgeview, IL 60455</p></>
    },
    {
      icon: Phone,
      title: 'Phone',
      content: <><p className="text-gray-400">(773) 690-2541</p><p className="text-gray-500 text-sm mt-1">Call or Text</p></>
    },
    {
      icon: Mail,
      title: 'Email',
      content: <p className="text-gray-400 break-all">tintedbyj@gmail.com</p>
    },
    {
      icon: Clock,
      title: 'Hours',
      content: <><p className="text-gray-400">Mon-Fri: 8AM - 6PM</p><p className="text-gray-400">Sat: 9AM - 4PM</p></>
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-gold-600/30 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-gold-500 font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">Contact Us</h2>
          <p className="text-gray-400 text-lg">
            Ready to upgrade your vehicle? Contact us today to schedule your appointment.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-white mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input 
                  type="text"
                  name="name"
                  placeholder="Name *"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-gray-500"
                />
                <Input 
                  type="tel"
                  name="phone"
                  placeholder="Phone *"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-gray-500"
                />
              </div>
              <Input 
                type="email"
                name="email"
                placeholder="Email *"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-slate-950 border-slate-800 text-white placeholder:text-gray-500"
              />
              <div className="grid sm:grid-cols-3 gap-4">
                <Input 
                  type="text"
                  name="year"
                  placeholder="Year *"
                  value={formData.year}
                  onChange={handleChange}
                  required
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-gray-500"
                />
                <Input 
                  type="text"
                  name="make"
                  placeholder="Make *"
                  value={formData.make}
                  onChange={handleChange}
                  required
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-gray-500"
                />
                <Input 
                  type="text"
                  name="model"
                  placeholder="Model *"
                  value={formData.model}
                  onChange={handleChange}
                  required
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-gray-500"
                />
              </div>
              <Textarea 
                name="message"
                placeholder="Message *"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="bg-slate-950 border-slate-800 text-white placeholder:text-gray-500"
              />
              <Button type="submit" disabled={isSubmitting} className="w-full bg-gold-500 hover:bg-gold-600 text-black font-semibold py-6 transition-all duration-300">
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
            
            {contactCards.map((card, idx) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 + idx * 0.1 }}
                  className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 hover:border-gold-600/50 transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gold-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-gold-500" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">{card.title}</h4>
                      {card.content}
                    </div>
                  </div>
                </motion.div>
              );
            })}

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.7 }}
              className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden mt-8"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_tint-masters-chi/artifacts/fzn982da_Screenshot%202025-12-22%20at%207.29.40%E2%80%AFAM.png" 
                alt="Tint Garage showcase"
                className="w-full h-auto object-cover"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
